import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Heart, Play, AlertTriangle, RotateCcw, ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
Alien & Zombies AR — real 360° mobile-first:
- getUserMedia rear camera (facingMode: environment, playsInline for iOS)
- DeviceOrientation for real 360° positioning — creatures live at fixed (azimuth, elevation)
- Player must physically rotate phone to find them
- 4 real character sprites with transparent PNG (alien F/M, zombie F/M)
- Tap-to-shoot with reticle-aware hit detection
*/

const CREATURES = [
  { id: "zombie_m", img: "/creatures/zombie-male.png",   speed: 0.9, hp: 1, points: 100 },
  { id: "zombie_f", img: "/creatures/zombie-female.png", speed: 1.1, hp: 1, points: 120 },
  { id: "alien_m",  img: "/creatures/alien-male.png",    speed: 1.5, hp: 2, points: 180 },
  { id: "alien_f",  img: "/creatures/alien-female.png",  speed: 1.8, hp: 2, points: 220 },
];

function angleDelta(a, b) { let d = ((a - b + 540) % 360) - 180; return d; }

export default function AlienZombies() {
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;
  const [phase, setPhase] = useState("intro");
  const [permError, setPermError] = useState(null);
  const [stats, setStats] = useState({ score: 0, kills: 0, wave: 1, combo: 1, lives: 3, best_combo: 1, aliens: 0, zombies: 0, shots: 0 });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const imgCacheRef = useRef({});
  const stateRef = useRef({
    heading: 0, pitch: 0,      // camera view direction
    baseHeading: null,          // first observed heading, used as reference
    enemies: [],                // {azimuth, elevation, dist(0-1 close), kindIdx, hp, id}
    projectiles: [],
    particles: [],
    lastSpawn: 0, waveEnemiesToSpawn: 5, running: false,
    lastComboAt: 0,
    dragging: false, lastDragX: 0, lastDragY: 0,
  });
  const statsRef = useRef(stats);
  statsRef.current = stats;

  // Preload creature images
  useEffect(() => {
    CREATURES.forEach(c => {
      const im = new Image();
      im.src = c.img;
      imgCacheRef.current[c.id] = im;
    });
  }, []);

  // Ask camera permission + iOS motion permission
  const startCamera = async () => {
    setPermError(null);
    // iOS 13+ DeviceOrientation permission (MUST be called from user gesture)
    if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
      try { await DeviceOrientationEvent.requestPermission(); } catch { /* noop */ }
    }
    if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
      try { await DeviceMotionEvent.requestPermission(); } catch { /* noop */ }
    }
    // Stop any previous stream
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("no-mediadevices");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
    } catch (e) {
      setPermError(e.name === "NotAllowedError" ? T.camDenied : T.camUnavailable);
    }
    stateRef.current = {
      heading: 0, pitch: 0, baseHeading: null,
      enemies: [], projectiles: [], particles: [],
      lastSpawn: 0, waveEnemiesToSpawn: 5, running: true, lastComboAt: 0,
      dragging: false, lastDragX: 0, lastDragY: 0,
    };
    setStats({ score: 0, kills: 0, wave: 1, combo: 1, lives: 3, best_combo: 1, aliens: 0, zombies: 0, shots: 0 });
    setPhase("playing");
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    const v = videoRef.current;
    if (v) { try { v.pause(); } catch { /* noop */ } v.srcObject = null; }
  };
  useEffect(() => () => stopCamera(), []);

  // Attach stream to the video element AFTER it's mounted (phase === "playing")
  useEffect(() => {
    if (phase !== "playing") return;
    const v = videoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;
    try {
      v.srcObject = s;
      v.setAttribute("playsinline", "true");
      v.setAttribute("webkit-playsinline", "true");
      v.muted = true;
      v.autoplay = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {
        // Autoplay could be blocked — retry on first user interaction
        const retry = () => { v.play().catch(() => {}); window.removeEventListener("touchend", retry); window.removeEventListener("click", retry); };
        window.addEventListener("touchend", retry, { once: true });
        window.addEventListener("click", retry, { once: true });
      });
    } catch { /* noop */ }
  }, [phase]);

  // Device orientation → heading + pitch
  // BUG FIX: Register only ONE source at a time. Previously we registered both
  // `deviceorientation` and `deviceorientationabsolute` which fire simultaneously
  // on Android Chrome with DIFFERENT reference frames — causing the heading to
  // jump back and forth. Result: creatures appeared to move AWAY when you turned
  // toward them. We now lock to the best available source and re-anchor on switch.
  useEffect(() => {
    if (phase !== "playing") return;

    const RANK = { ios: 3, abs: 2, rel: 1 };
    let currentSource = null;

    const getScreenAngle = () => {
      if (typeof screen !== "undefined" && screen.orientation && typeof screen.orientation.angle === "number") {
        return screen.orientation.angle;
      }
      return window.orientation || 0;
    };

    const applyHeading = (rawHeading, beta) => {
      const sa = getScreenAngle();
      const heading = (rawHeading + sa + 360) % 360;
      const st = stateRef.current;
      if (st.baseHeading == null) st.baseHeading = heading;
      st.heading = (heading - st.baseHeading + 360) % 360;
      if (beta != null) {
        st.pitch = Math.max(-45, Math.min(45, beta - 75));
      }
    };

    const onOrient = (e) => {
      // Determine what source this event provides
      let source, rawHeading;
      if (e.webkitCompassHeading != null) {
        source = "ios";
        rawHeading = e.webkitCompassHeading;
      } else if (e.absolute === true && e.alpha != null) {
        source = "abs";
        rawHeading = 360 - e.alpha;
      } else if (e.alpha != null) {
        source = "rel";
        rawHeading = 360 - e.alpha;
      } else {
        return;
      }

      // Only accept events from the best-ranked source seen so far.
      // If a better source arrives, upgrade and re-anchor.
      if (currentSource == null || RANK[source] > RANK[currentSource]) {
        currentSource = source;
        stateRef.current.baseHeading = null; // re-anchor to new source's frame
      } else if (source !== currentSource) {
        return; // ignore lower-ranked event
      }

      applyHeading(rawHeading, e.beta);
    };

    window.addEventListener("deviceorientation", onOrient, true);
    window.addEventListener("deviceorientationabsolute", onOrient, true);
    return () => {
      window.removeEventListener("deviceorientation", onOrient, true);
      window.removeEventListener("deviceorientationabsolute", onOrient, true);
    };
  }, [phase]);

  // Touch/mouse drag fallback → allows rotating the camera view on devices
  // where DeviceOrientation is unavailable, denied, or on desktop.
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const start = (evt) => {
      const t = evt.touches?.[0] || evt;
      stateRef.current.dragging = true;
      stateRef.current.lastDragX = t.clientX;
      stateRef.current.lastDragY = t.clientY;
      stateRef.current.dragMoved = false;
    };
    const move = (evt) => {
      if (!stateRef.current.dragging) return;
      const t = evt.touches?.[0] || evt;
      const dx = t.clientX - stateRef.current.lastDragX;
      const dy = t.clientY - stateRef.current.lastDragY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) stateRef.current.dragMoved = true;
      stateRef.current.lastDragX = t.clientX;
      stateRef.current.lastDragY = t.clientY;
      // 1 px = ~0.25° rotation
      stateRef.current.heading = (stateRef.current.heading + dx * 0.25 + 360) % 360;
      stateRef.current.pitch = Math.max(-45, Math.min(45, stateRef.current.pitch - dy * 0.2));
    };
    const end = () => { stateRef.current.dragging = false; };

    canvas.addEventListener("touchstart", start, { passive: true });
    canvas.addEventListener("touchmove", move, { passive: true });
    canvas.addEventListener("touchend", end);
    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    return () => {
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("mouseup", end);
    };
  }, [phase]);

  // Game loop
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    const FOV = 70; // degrees horizontal FOV

    const spawnEnemy = () => {
      const s = statsRef.current;
      // Place enemy randomly around the player (360° azimuth), slight elevation variation
      // NOT centered on current heading — user must find them
      const az = Math.random() * 360;
      const el = (Math.random() - 0.5) * 25;
      const kindIdx = Math.floor(Math.random() * (s.wave >= 3 ? 4 : s.wave >= 2 ? 3 : 2));
      stateRef.current.enemies.push({
        id: Math.random(),
        kindIdx,
        azimuth: az, elevation: el,
        dist: 0.05,
        speedMul: 1 + s.wave * 0.12 + Math.random() * 0.2,
        hp: CREATURES[kindIdx].hp,
      });
    };

    const damage = (enemy, cx, cy) => {
      enemy.hp -= 1;
      // Particles
      for (let i = 0; i < 10; i++) {
        stateRef.current.particles.push({
          x: cx, y: cy,
          vx: (Math.random() - 0.5) * 6, vy: (Math.random() - 0.5) * 6 - 2,
          life: 1, color: "#fbbf24",
        });
      }
      if (enemy.hp <= 0) {
        stateRef.current.enemies = stateRef.current.enemies.filter(e => e.id !== enemy.id);
        for (let i = 0; i < 22; i++) {
          stateRef.current.particles.push({
            x: cx, y: cy,
            vx: (Math.random() - 0.5) * 10, vy: (Math.random() - 0.5) * 10 - 3,
            life: 1, color: enemy.kindIdx >= 2 ? "#a855f7" : "#22c55e",
          });
        }
        setStats(s => {
          const now = Date.now();
          const combo = now - stateRef.current.lastComboAt < 2000 ? s.combo + 1 : 1;
          stateRef.current.lastComboAt = now;
          const isAlien = enemy.kindIdx >= 2;
          const points = CREATURES[enemy.kindIdx].points * combo;
          const newKills = s.kills + 1;
          const newLives = newKills > 0 && newKills % 10 === 0 && s.lives < 5 ? s.lives + 1 : s.lives;
          if (newLives > s.lives) toast.success(T.lifeGained, { duration: 1200 });
          return {
            ...s, score: s.score + points, kills: newKills, combo,
            best_combo: Math.max(s.best_combo, combo), lives: newLives,
            aliens: isAlien ? s.aliens + 1 : s.aliens,
            zombies: isAlien ? s.zombies : s.zombies + 1,
          };
        });
      }
    };

    const handleTap = (evt) => {
      evt.preventDefault?.();
      // Ignore if the touch was a drag (rotation gesture)
      if (stateRef.current.dragMoved) { stateRef.current.dragMoved = false; return; }
      const rect = canvas.getBoundingClientRect();
      const src = evt.changedTouches?.[0] || evt.touches?.[0] || evt;
      const tx = src.clientX - rect.left;
      const ty = src.clientY - rect.top;
      setStats(s => ({ ...s, shots: s.shots + 1 }));

      // Which enemy did we hit? Check visible enemies with bounding boxes
      const cx0 = canvas.width / 2, cy0 = canvas.height / 2;
      const st = stateRef.current;
      const visible = st.enemies.map(e => {
        const dAz = angleDelta(e.azimuth, st.heading);
        const dEl = e.elevation - st.pitch;
        if (Math.abs(dAz) > FOV / 2 + 5) return null;
        const sx = cx0 + (dAz / (FOV / 2)) * (canvas.width / 2);
        const sy = cy0 - (dEl / 30) * (canvas.height / 4);
        const size = 40 + e.dist * 220;
        return { e, sx, sy, size };
      }).filter(Boolean).sort((a, b) => b.e.dist - a.e.dist);

      for (const v of visible) {
        if (Math.abs(tx - v.sx) < v.size * 0.6 && Math.abs(ty - v.sy) < v.size * 0.9) {
          damage(v.e, tx, ty);
          st.projectiles.push({ x: tx, y: ty, life: 1, color: "#fbbf24" });
          return;
        }
      }
      st.projectiles.push({ x: tx, y: ty, life: 0.5, color: "#94a3b8" });
    };

    canvas.addEventListener("touchend", handleTap, { passive: false });
    canvas.addEventListener("click", handleTap);

    const loop = () => {
      const s = statsRef.current;
      const st = stateRef.current;
      if (!st.running) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const now = Date.now();
      const spawnRate = Math.max(600, 1600 - s.wave * 120);
      if (st.waveEnemiesToSpawn > 0 && now - st.lastSpawn > spawnRate) {
        spawnEnemy();
        st.lastSpawn = now;
        st.waveEnemiesToSpawn--;
      }
      if (st.waveEnemiesToSpawn === 0 && st.enemies.length === 0) {
        setStats(cur => ({ ...cur, wave: cur.wave + 1 }));
        st.waveEnemiesToSpawn = 5 + (s.wave + 1) * 2;
        toast(`🌊 ${T.wave} ${s.wave + 1}!`, { duration: 1200 });
      }

      // Move enemies (they approach)
      st.enemies.forEach(e => {
        e.dist += 0.0014 * CREATURES[e.kindIdx].speed * e.speedMul;
        if (e.dist >= 1) {
          st.enemies = st.enemies.filter(x => x.id !== e.id);
          setStats(cur => {
            const lives = cur.lives - 1;
            if (lives <= 0) { st.running = false; setPhase("gameover"); }
            return { ...cur, lives, combo: 1 };
          });
        }
      });

      // Draw enemies in current FOV
      const cx0 = canvas.width / 2, cy0 = canvas.height / 2;
      const sorted = [...st.enemies].sort((a, b) => a.dist - b.dist);
      sorted.forEach(e => {
        const dAz = angleDelta(e.azimuth, st.heading);
        const dEl = e.elevation - st.pitch;
        if (Math.abs(dAz) > FOV / 2 + 5) return;
        const sx = cx0 + (dAz / (FOV / 2)) * (canvas.width / 2);
        const sy = cy0 - (dEl / 30) * (canvas.height / 4);
        const size = 40 + e.dist * 220;
        // Shadow
        ctx.fillStyle = "rgba(0,0,0,0.35)";
        ctx.beginPath();
        ctx.ellipse(sx, sy + size * 0.9, size * 0.6, size * 0.14, 0, 0, Math.PI * 2);
        ctx.fill();
        // Sprite
        const img = imgCacheRef.current[CREATURES[e.kindIdx].id];
        if (img && img.complete) {
          const aspect = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0.6;
          const h = size * 1.9;
          const w = h * aspect;
          ctx.drawImage(img, sx - w / 2, sy - h * 0.85, w, h);
        } else {
          ctx.fillStyle = e.kindIdx >= 2 ? "#a855f7" : "#4d7c4d";
          ctx.fillRect(sx - size / 2, sy - size, size, size * 1.6);
        }
        // HP bar for tougher
        const meta = CREATURES[e.kindIdx];
        if (e.hp < meta.hp) {
          ctx.fillStyle = "rgba(0,0,0,0.6)";
          ctx.fillRect(sx - size * 0.5, sy - size * 1.05, size, 4);
          ctx.fillStyle = "#22c55e";
          ctx.fillRect(sx - size * 0.5, sy - size * 1.05, size * (e.hp / meta.hp), 4);
        }
      });

      // Off-screen indicators (arrows)
      st.enemies.forEach(e => {
        const dAz = angleDelta(e.azimuth, st.heading);
        if (Math.abs(dAz) <= FOV / 2) return;
        const dir = dAz > 0 ? 1 : -1;
        const x = dir > 0 ? canvas.width - 30 : 30;
        const y = cy0 + (e.elevation - st.pitch) * -2;
        ctx.fillStyle = e.kindIdx >= 2 ? "#a855f7" : "#22c55e";
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - dir * 20, y - 10);
        ctx.lineTo(x - dir * 20, y + 10);
        ctx.closePath();
        ctx.fill();
      });

      // Projectiles
      st.projectiles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.strokeStyle = p.color; ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 30 * (1 - p.life + 0.3), 0, Math.PI * 2);
        ctx.stroke();
        p.life -= 0.07;
        ctx.globalAlpha = 1;
      });
      st.projectiles = st.projectiles.filter(p => p.life > 0);

      // Particles
      st.particles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill();
        p.x += p.vx; p.y += p.vy; p.vy += 0.3; p.life -= 0.02;
        ctx.globalAlpha = 1;
      });
      st.particles = st.particles.filter(p => p.life > 0);

      // Reticle + compass heading
      ctx.strokeStyle = "rgba(255,255,255,0.4)"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(cx0, cy0, 42, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx0 - 16, cy0); ctx.lineTo(cx0 + 16, cy0);
      ctx.moveTo(cx0, cy0 - 16); ctx.lineTo(cx0, cy0 + 16);
      ctx.stroke();
      // heading text
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.font = "12px monospace";
      ctx.textAlign = "center";
      ctx.fillText(`${Math.round(st.heading)}°`, cx0, cy0 - 55);

      animId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(animId);
      canvas.removeEventListener("touchend", handleTap);
      canvas.removeEventListener("click", handleTap);
      window.removeEventListener("resize", resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Submit score at game over
  useEffect(() => {
    if (phase !== "gameover") return;
    stopCamera();
    axios.post(`${API}/games/scores/submit`, {
      game_id: "alien-zombies",
      score: stats.score, kills: stats.kills, zombies: stats.zombies, aliens: stats.aliens,
      max_combo: stats.best_combo, waves_reached: stats.wave, shots_fired: stats.shots,
    }, { withCredentials: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-red-900 text-white flex flex-col" data-testid="alien-zombies-intro">
        <div className="p-4"><Link to="/games" className="flex items-center gap-2 text-white/80"><ArrowLeft size={16} /> {T.back}</Link></div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="text-8xl mb-6 animate-pulse">👽🧟</div>
            <h1 className="font-serif-mystery text-4xl md:text-5xl mb-3">Alien & Zombies</h1>
            <p className="text-white/80 mb-6">{T.introText}</p>
            <div className="p-4 rounded-xl bg-amber-500/20 border border-amber-500/50 mb-6 text-left flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-300 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-100">{T.warning}</p>
            </div>
            <Button data-testid="start-game-btn" onClick={startCamera} size="lg"
              className="bg-red-600 hover:bg-red-700 text-white rounded-full h-14 px-8 text-lg shadow-2xl">
              <Play size={20} className="mr-2" /> {T.start}
            </Button>
            <p className="text-xs text-white/50 mt-3">{T.tipMobile}</p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "gameover") {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6" data-testid="game-over-screen">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">💀</div>
          <h1 className="font-serif-mystery text-4xl mb-6 text-red-500">GAME OVER</h1>
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[["Score", stats.score.toLocaleString(), "go-score"], [T.kills, stats.kills, "go-kills"], [T.wave, stats.wave, "go-wave"], [T.bestCombo, `x${stats.best_combo}`, "go-combo"]].map(([l, v, tid]) => (
              <div key={tid} className="border border-white/20 rounded-xl p-3 bg-white/5" data-testid={tid}>
                <div className="text-[10px] uppercase tracking-widest opacity-70">{l}</div>
                <div className="font-serif-mystery text-2xl">{v}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button data-testid="replay-btn" onClick={startCamera} className="bg-red-600 hover:bg-red-700 rounded-full h-11 px-5"><RotateCcw size={16} className="mr-2" /> {T.replay}</Button>
            <Link to="/games/leaderboard"><Button variant="outline" className="rounded-full h-11 border-white/30 text-white hover:bg-white/10">🏆 {T.leaderboard}</Button></Link>
            <Link to="/games"><Button variant="outline" className="rounded-full h-11 border-white/30 text-white hover:bg-white/10">🎮 {T.backGames}</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black overflow-hidden" data-testid="alien-zombies-playing">
      {/* Fallback gradient behind the video — visible if camera is loading or unavailable */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900" />
      <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted autoPlay />
      {permError && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-red-900/80 border border-red-500 rounded-xl p-3 max-w-sm text-center text-white text-sm z-10" data-testid="cam-error">
          <AlertTriangle size={16} className="inline mr-1" /> {permError}
        </div>
      )}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full touch-none" />

      <div className="absolute top-4 left-0 right-0 px-4 flex justify-between items-start pointer-events-none">
        <div className="bg-black/60 backdrop-blur rounded-2xl px-4 py-2 text-white">
          <div className="text-[10px] uppercase tracking-widest opacity-70">{T.score}</div>
          <div className="font-serif-mystery text-2xl" data-testid="hud-score">{stats.score.toLocaleString()}</div>
        </div>
        <div className="bg-black/60 backdrop-blur rounded-2xl px-4 py-2 text-white text-center">
          <div className="text-[10px] uppercase tracking-widest opacity-70">🌊 {T.wave}</div>
          <div className="font-serif-mystery text-2xl" data-testid="hud-wave">{stats.wave}</div>
        </div>
        <div className="bg-black/60 backdrop-blur rounded-2xl px-4 py-2 text-white text-right">
          <div className="text-[10px] uppercase tracking-widest opacity-70">{T.combo}</div>
          <div className="font-serif-mystery text-2xl text-orange-400" data-testid="hud-combo">x{stats.combo}</div>
        </div>
      </div>

      <div className="absolute bottom-4 left-0 right-0 px-4 flex justify-between items-end pointer-events-none">
        <div className="flex gap-1" data-testid="hud-lives">
          {Array.from({ length: stats.lives }).map((_, i) => <Heart key={i} size={26} className="text-red-500 drop-shadow-xl" fill="currentColor" />)}
        </div>
        <div className="bg-black/60 backdrop-blur rounded-2xl px-3 py-1.5 text-white text-xs">👾 {stats.kills}</div>
      </div>

      <div className="absolute top-20 left-1/2 -translate-x-1/2 text-white/70 text-xs bg-black/40 px-3 py-1 rounded-full pointer-events-none">
        {T.rotateHint}
      </div>
    </div>
  );
}

const FR = {
  back: "Retour",
  introText: "Ta caméra devient un portail. Zombies et aliens apparaissent tout autour de toi à 360°. Tourne physiquement ton téléphone pour les trouver, vise avec le réticule, tire.",
  warning: "⚠️ Attention à ton environnement réel. Ne joue pas en conduisant, en traversant une rue ou dans un endroit dangereux. Respecte les propriétés privées.",
  start: "🎮 Autoriser la caméra et démarrer",
  tipMobile: "Fonctionne mieux sur mobile avec la caméra arrière.",
  camDenied: "Permission caméra refusée. Active-la dans les paramètres du navigateur.",
  camUnavailable: "Caméra indisponible — mode compatibilité (fond simulé).",
  score: "Score", wave: "Vague", combo: "Combo", kills: "Éliminations", bestCombo: "Meilleur combo",
  replay: "Rejouer", leaderboard: "Classement", backGames: "Retour aux jeux",
  lifeGained: "❤️ +1 VIE !",
  rotateHint: "↻ Tourne ton téléphone OU glisse le doigt pour regarder autour",
};
const EN = {
  back: "Back",
  introText: "Your camera becomes a portal. Zombies and aliens appear all around you in 360°. Physically rotate your phone to find them, aim with the reticle, shoot.",
  warning: "⚠️ Watch your real environment. Do not play while driving, crossing streets or in dangerous places. Respect private property.",
  start: "🎮 Allow camera & start",
  tipMobile: "Works best on mobile with rear camera.",
  camDenied: "Camera permission denied. Enable it in your browser settings.",
  camUnavailable: "Camera unavailable — compatibility mode (simulated background).",
  score: "Score", wave: "Wave", combo: "Combo", kills: "Kills", bestCombo: "Best combo",
  replay: "Replay", leaderboard: "Leaderboard", backGames: "Back to games",
  lifeGained: "❤️ +1 LIFE!",
  rotateHint: "↻ Rotate your phone OR swipe to look around",
};
